import React, { useState } from 'react';
import { FingotiButton, FingotiModalActions, FingotiModalContent } from '@fingoti/components';
import { useSnackbar } from 'notistack';
import clsx from 'clsx';

import makeStyles from '@material-ui/core/styles/makeStyles';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Button from '@material-ui/core/Button';

import Validation from '../../services/validation.service';
import { apiService } from '../../services/api.service';
import { useForceUpdate } from '../Utils/ForceUpdate';
import { roleScopes } from './roleScopes';

const useStyles = makeStyles((theme) => ({

    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    table: {
        width: '100%',
        overflowX: 'hidden',
        [theme.breakpoints.up('md')]: {
            width: '75%',
            overflowX: 'unset',
        },
    },
    headerCell: {
        border: 'none',
        textAlign: 'right',
        borderRight: '1px solid ' + theme.palette.greyFour.main,
        color: theme.palette.greyTwo.main,
        [theme.breakpoints.down('md')]: {
            paddingLeft: 0,
            paddingRight: theme.spacing(2),
        }
    },
    noBottomBorder: {
        border: 'none',
        [theme.breakpoints.up('sm')]: {
            width: '55%',
        },
        [theme.breakpoints.down('md')]: {
            paddingLeft: theme.spacing(1),
            paddingRight: 0,
        }
    },
    roleName: {
        width: '100%',
        marginBottom: theme.spacing(4),
        [theme.breakpoints.up('md')]: {
            width: '50%'
        }
    },
    buttonGroup: {
        display: 'flex',
        justifyContent: 'space-evenly',
        width: '100%',
        [theme.breakpoints.up('md')]: {
            width: '50%',
        }
    },
    button: {
        width: '42%',
    },
    roleButtonGroup: {
        '&:not(:last-child)': {
            borderRight: '1px solid #ccc',
        }
    },
    textRoleButton: {
        padding: '6px 16px',
    }

}))

const initalRole = {
    roleName: '',
    scopes: [],
}

export const OrganisationRoleAdd = ({ handleNewRole, setOpen }) => {

    const classes = useStyles();
    const forceUpdate = useForceUpdate();
    const { enqueueSnackbar } = useSnackbar();
    const [role, setRole] = useState(initalRole)
    const [errors, setErrors] = useState({});

    const handleSetErrors = (errors) => {
        setErrors(errors);
        forceUpdate();
    }

    const validate = new Validation(errors, handleSetErrors);

    const handleChangeRoleName = (newName) => {
        var newRole = { ...role };
        newRole['roleName'] = newName;
        setRole(newRole)
    }

    // 0 = None, 1 = Read, 2 = Write
    const levelFor = (scopes, realm) => {
        if (!Array.isArray(scopes)) return 0;
        if (scopes.includes(`${realm}:write`)) return 2;
        if (scopes.includes(`${realm}:read`)) return 1;
        return 0;
    }

    const handleChangeRole = (realm, level) => {
        // Drop both scopes for this realm, then add back per the chosen level.
        // Write implies read, so Write adds both; Read adds only read.
        const scopes = (role.scopes ?? []).filter(
            (s) => s !== `${realm}:read` && s !== `${realm}:write`
        );
        if (level >= 1) scopes.push(`${realm}:read`);
        if (level >= 2) scopes.push(`${realm}:write`);
        setRole({ ...role, scopes });
    }

    const submitNewRole = () => {
        let body = { ...role }
        apiService.postData(`/organisation/roles`, body)
            .then(result => {
                enqueueSnackbar(result.message, { variant: 'success' })
                handleNewRole();
            })
            .catch(error => {
                enqueueSnackbar(error, { variant: 'error' })
            })
    }

    return (
        <>
            <FingotiModalContent>
                <div className={classes.root}>
                    <FormControl variant="outlined" className={classes.roleName}>
                        <TextField
                            fullWidth
                            required
                            id='roleName'
                            name='roleName'
                            variant='outlined'
                            label='Role Name'
                            value={role.roleName}
                            onChange={(e) => handleChangeRoleName(e.target.value)}
                            onBlur={(e) => validate.checkNotEmpty(e)}
                            error={Boolean(errors['roleName'])}
                            helperText={Boolean(errors['roleName']) ? errors['roleName'].msg : ""}
                        />
                    </FormControl>
                    <TableContainer className={classes.table}>
                        <Table size="small">
                            <TableBody>
                                {roleScopes.map((scope) => {
                                    const level = levelFor(role.scopes, scope);
                                    return (
                                        <TableRow key={scope}>
                                            <TableCell className={classes.headerCell}>{scope}</TableCell>
                                            <TableCell className={classes.noBottomBorder}>
                                                <ButtonGroup disableElevation>
                                                    <Button
                                                        color="default"
                                                        variant={level === 0 ? "contained" : "text"}
                                                        onClick={() => handleChangeRole(scope, 0)}
                                                        className={clsx(classes.textRoleButton, classes.roleButtonGroup)}
                                                    >
                                                        None
                                                    </Button>
                                                    <Button
                                                        color={level === 1 ? "secondary" : "default"}
                                                        variant={level === 1 ? "contained" : "text"}
                                                        onClick={() => handleChangeRole(scope, 1)}
                                                        className={clsx(classes.textRoleButton, classes.roleButtonGroup)}
                                                    >
                                                        Read
                                                    </Button>
                                                    <Button
                                                        color={level === 2 ? "primary" : "default"}
                                                        variant={level === 2 ? "contained" : "text"}
                                                        onClick={() => handleChangeRole(scope, 2)}
                                                        className={clsx(classes.textRoleButton, classes.roleButtonGroup)}
                                                    >
                                                        Write
                                                    </Button>
                                                </ButtonGroup>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            </FingotiModalContent>
            <FingotiModalActions>
                <div className={classes.buttonGroup}>
                    <FingotiButton
                        light
                        color="primary"
                        className={classes.button}
                        onClick={() => submitNewRole()}
                    >
                        ADD ROLE
                    </FingotiButton>
                    <FingotiButton
                        light
                        className={classes.button}
                        onClick={() => setOpen(false)}
                    >
                        CANCEL
                    </FingotiButton>
                </div>
            </FingotiModalActions>
        </>
    )
}
